import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
} from 'firebase/firestore';
import { db } from '../../config/config';
import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Collapse,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/ReactToastify.css';
import Header from '../Hub/components/Header';
import PageHeading from '../../components/PageHeading';
import StandaloneBackButton from '../../components/StandaloneBackButton';
import { filterPanel } from '../../utils/surfaces';
import AddGameModal from './components/AddGameModel';
import EditGameModal from './components/EditGameModel';
import ConfirmDeleteGameModal from './components/ConfirmDeleteGameModel';
import GameCard from './components/GameCard';

interface Game {
  id: string;
  title: string;
  year: number;
  image: string;
  platform: string;
  edition: string;
  editionName?: string;
  steelbook: boolean;
  sealed: boolean;
}

const PhysicalGameCollection = () => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [showFilters, setShowFilters] = useState(false);

  const [games, setGames] = useState<Game[]>([]);
  const [filterPlatform, setFilterPlatform] = useState<string>('PS5');
  const [filterEdition, setFilterEdition] = useState<string>('All');
  const [filterSealed, setFilterSealed] = useState<string>('All');
  const [filterSteelbook, setFilterSteelbook] = useState<string>('All');
  const [sortOption, setSortOption] = useState<string>('title-asc');

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isMobile) {
      setShowFilters(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const ref = collection(db, 'PhysicalGameCollection');
        const snapshot = await getDocs(ref);

        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Game[];

        setGames(list);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, []);

  const filteredGames = useMemo(() => {
    let sorted = [...games];

    switch (sortOption) {
      case 'title-asc':
        sorted.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'title-desc':
        sorted.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case 'year-asc':
        sorted.sort((a, b) => a.year - b.year);
        break;
      case 'year-desc':
        sorted.sort((a, b) => b.year - a.year);
        break;
    }

    return sorted.filter((g) => {
      const matchesPlatform =
        filterPlatform === 'All' || g.platform === filterPlatform;

      const matchesEdition =
        filterEdition === 'All' || g.edition === filterEdition;

      const matchesSealed =
        filterSealed === 'All' ||
        (filterSealed === 'Yes' && g.sealed) ||
        (filterSealed === 'No' && !g.sealed);

      const matchesSteelbook =
        filterSteelbook === 'All' ||
        (filterSteelbook === 'Yes' && g.steelbook) ||
        (filterSteelbook === 'No' && !g.steelbook);

      return (
        matchesPlatform &&
        matchesEdition &&
        matchesSealed &&
        matchesSteelbook
      );
    });
  }, [
    games,
    sortOption,
    filterPlatform,
    filterEdition,
    filterSealed,
    filterSteelbook,
  ]);

  const handleAddGame = async (newGame: Game) => {
    const normalizedEditionName =
      newGame.edition === 'Special' ? (newGame.editionName || '').trim() : '';

    const exists = games.find(
      (g) =>
        g.title.toLowerCase() === newGame.title.toLowerCase() &&
        g.year === newGame.year &&
        g.platform === newGame.platform &&
        g.edition === newGame.edition &&
        (g.edition === 'Special' ? (g.editionName || '').trim() : '') ===
          normalizedEditionName &&
        g.steelbook === newGame.steelbook &&
        g.sealed === newGame.sealed
    );

    if (exists) {
      toast.error(`"${newGame.title}" already exists for ${newGame.platform}`);
      return;
    }

    try {
      const gameToSave = {
        ...newGame,
        editionName: normalizedEditionName,
      };

      const ref = collection(db, 'PhysicalGameCollection');
      const docRef = await addDoc(ref, gameToSave);
      setGames((prev) => [...prev, { ...gameToSave, id: docRef.id }]);
      toast.success(`Added "${newGame.title}"`);
    } catch {
      toast.error('Failed to add game.');
    }
  };

  const handleEditGame = async (updated: Game) => {
    try {
      const updatedGame = {
        ...updated,
        editionName:
          updated.edition === 'Special'
            ? (updated.editionName || '').trim()
            : '',
      };

      const ref = doc(db, 'PhysicalGameCollection', updated.id);
      await updateDoc(ref, updatedGame as Partial<Game>);
      setGames((prev) =>
        prev.map((g) => (g.id === updated.id ? updatedGame : g))
      );
      toast.success(`Updated "${updated.title}"`);
    } catch {
      toast.error('Failed to update game.');
    }
  };

  const handleDeleteGame = async () => {
    if (!selectedGame) return;

    try {
      const ref = doc(db, 'PhysicalGameCollection', selectedGame.id);
      await deleteDoc(ref);
      setGames((prev) => prev.filter((g) => g.id !== selectedGame.id));
      setShowDeleteModal(false);
      toast.success(`Deleted "${selectedGame.title}"`);
    } catch {
      toast.error('Failed to delete game.');
    }
  };

  const handleCheckValue = useCallback((game: Game) => {
    const specialPart =
      game.edition === 'Special'
        ? ` ${game.editionName?.trim() || 'Special Edition'}`
        : '';

    const search = `${game.title}${specialPart} ${game.platform}`;
    const query = encodeURIComponent(search);

    const url = `https://www.ebay.ie/sch/i.html?_nkw=${query}&LH_Sold=1&LH_Complete=1`;

    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleHowLongToBeat = useCallback((game: Game) => {
    const search = encodeURIComponent(game.title);
    const url = `https://howlongtobeat.com/?q=${search}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilterPlatform('All');
    setFilterEdition('All');
    setFilterSealed('All');
    setFilterSteelbook('All');
    setSortOption('title-asc');
  }, []);

  const handleMenuClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, game: Game) => {
      setAnchorEl(e.currentTarget);
      setSelectedGame(game);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const hasActiveFilters =
    filterPlatform !== 'All' ||
    filterEdition !== 'All' ||
    filterSealed !== 'All' ||
    filterSteelbook !== 'All' ||
    sortOption !== 'title-asc';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header />

      <Box sx={{ p: 3, px: { lg: 5 } }}>
        <ToastContainer />

        <StandaloneBackButton />
        <PageHeading>Physical Game Collection</PageHeading>

        <Box sx={{ mb: 3 }}>
          {isMobile && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters((prev) => !prev)}
                sx={{ flex: 1 }}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>

              <Button
                variant="contained"
                onClick={() => setShowAddModal(true)}
                sx={{ flex: 1 }}
              >
                Add Game
              </Button>
            </Box>
          )}

          <Collapse in={!isMobile || showFilters}>
            <Box
              sx={(theme) => ({
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                p: 2,
                justifyContent: 'space-between',
                ...filterPanel(theme),
              })}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  flex: 1,
                }}
              >
                <FormControl
                  size="small"
                  sx={{ minWidth: 160, width: isMobile ? '100%' : 'auto' }}
                >
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortOption}
                    label="Sort By"
                    onChange={(e) => setSortOption(e.target.value)}
                  >
                    <MenuItem value="title-asc">Title (A–Z)</MenuItem>
                    <MenuItem value="title-desc">Title (Z–A)</MenuItem>
                    <MenuItem value="year-asc">Year ↑</MenuItem>
                    <MenuItem value="year-desc">Year ↓</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ minWidth: 160, width: isMobile ? '100%' : 'auto' }}
                >
                  <InputLabel>Platform</InputLabel>
                  <Select
                    value={filterPlatform}
                    label="Platform"
                    onChange={(e) => setFilterPlatform(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="PS1">PS1</MenuItem>
                    <MenuItem value="PS2">PS2</MenuItem>
                    <MenuItem value="PS3">PS3</MenuItem>
                    <MenuItem value="PS4">PS4</MenuItem>
                    <MenuItem value="PS5">PS5</MenuItem>
                    <MenuItem value="Switch">Switch</MenuItem>
                    <MenuItem value="Switch 2">Switch 2</MenuItem>
                    <MenuItem value="DS">DS</MenuItem>
                    <MenuItem value="3DS">3DS</MenuItem>
                    <MenuItem value="PC">PC</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ minWidth: 140, width: isMobile ? '100%' : 'auto' }}
                >
                  <InputLabel>Edition</InputLabel>
                  <Select
                    value={filterEdition}
                    label="Edition"
                    onChange={(e) => setFilterEdition(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Standard">Standard</MenuItem>
                    <MenuItem value="Special">Special</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ minWidth: 140, width: isMobile ? '100%' : 'auto' }}
                >
                  <InputLabel>Sealed</InputLabel>
                  <Select
                    value={filterSealed}
                    label="Sealed"
                    onChange={(e) => setFilterSealed(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Yes">Sealed</MenuItem>
                    <MenuItem value="No">Unsealed</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ minWidth: 140, width: isMobile ? '100%' : 'auto' }}
                >
                  <InputLabel>Steelbook</InputLabel>
                  <Select
                    value={filterSteelbook}
                    label="Steelbook"
                    onChange={(e) => setFilterSteelbook(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Yes">Steelbook</MenuItem>
                    <MenuItem value="No">No Steelbook</MenuItem>
                  </Select>
                </FormControl>

                {hasActiveFilters && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearFilters}
                    sx={{ alignSelf: 'center' }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>

              {!isMobile && (
                <Button
                  variant="contained"
                  onClick={() => setShowAddModal(true)}
                >
                  Add Game
                </Button>
              )}
            </Box>
          </Collapse>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 3,
              px: { xs: 1.5, sm: 2 },
              width: '100%',
            }}
          >
            {filteredGames.map((game) => (
              <GameCard
                key={game.id}
                game={game}
                onMenuClick={handleMenuClick}
              />
            ))}
          </Box>
        )}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              setShowEditModal(true);
              handleMenuClose();
            }}
          >
            Edit
          </MenuItem>

          <MenuItem
            onClick={() => {
              if (selectedGame) handleCheckValue(selectedGame);
              handleMenuClose();
            }}
          >
            Check Value
          </MenuItem>

          <MenuItem
            onClick={() => {
              if (selectedGame) handleHowLongToBeat(selectedGame);
              handleMenuClose();
            }}
          >
            How Long to Beat
          </MenuItem>

          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              setShowDeleteModal(true);
              handleMenuClose();
            }}
          >
            Delete
          </MenuItem>
        </Menu>

        {showAddModal && (
          <AddGameModal
            show={showAddModal}
            onHide={() => setShowAddModal(false)}
            onAddGame={handleAddGame}
          />
        )}

        {selectedGame && (
          <EditGameModal
            show={showEditModal}
            onHide={() => setShowEditModal(false)}
            game={selectedGame}
            onEditGame={handleEditGame}
          />
        )}

        {selectedGame && (
          <ConfirmDeleteGameModal
            show={showDeleteModal}
            onHide={() => setShowDeleteModal(false)}
            onConfirm={handleDeleteGame}
            gameTitle={selectedGame.title}
          />
        )}
      </Box>
    </Box>
  );
};

export default PhysicalGameCollection;